import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container, Typography, Box, Button, IconButton, Tooltip, alpha, useTheme
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Stars as StarsIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import TableList from '../../common/TableList';
import ListBulkToolbar from '../../common/ListBulkToolbar';
import { SPONSOR_TIER_BULK_CONFIG } from '../../../utils/bulkUpdateConfigs';
import { useListBulkSelection, bulkTableProps, createBulkHandlers } from '../../../utils/listBulkSelection';

import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import { SUCCESS } from '../../common/SaaSTokens';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { canAccessPartnershipManagement } from '../../../utils/partnershipPermissions';

const NEON3 = '#34D399';
const SURFACE_MID = '#0a1628';

export default function SponsorTierList() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user: currentUser } = useAuth();
const { selectedIds, setSelectedIds, clearSelection } = useListBulkSelection();
  
  const hasPartnershipAccess = canAccessPartnershipManagement(currentUser);

  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [delOpen, setDelOpen] = useState(false);
  const [delLoading, setDelLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchTiers = async ({ forceRefresh = false } = {}) => {
    try {
      setLoading(true);
      const { data } = await api.get('/sponsor-tiers', forceRefresh ? { cache: false } : {});
      setTiers(Array.isArray(data) ? data.sort((a, b) => Number(b.sponsor_amount) - Number(a.sponsor_amount)) : []);
    } catch {
      setError('Failed to load sponsor tiers.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return; }
    if (!hasPartnershipAccess) { navigate('/dashboard'); return; }
    fetchTiers({ forceRefresh: Boolean(location.state?.refreshList) });
  }, [currentUser, hasPartnershipAccess, location.pathname, location.state?.refreshList]);

  const confirmDelete = async () => {
    try {
      setDelLoading(true);
      await api.delete(`/sponsor-tiers/${selected.id}`);
      setTiers(tiers.filter(t => t.id !== selected.id));
      setSuccess(`Deleted tier "${selected.tier_name}".`);
      setDelOpen(false);
      setSelected(null);
    } catch (e) {
      const msg = e.response?.data?.message || 'Delete failed.';
      setError(msg);
      setDelOpen(false);
    } finally { setDelLoading(false); }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = (tier) => {
    setSelected(tier);
    setDelOpen(true);
  };

  // Define columns for TableList
  const columns = [
    {
      key: 'tier_name',
      sortKey: 'tier_name',
      label: 'Tier',
      icon: StarsIcon,
      minWidth: 180,
      render: (tier) => (
        <Typography 
          variant="body2" 
          sx={{ 
            color: alpha(theme.palette.common.white, 0.95),
            fontWeight: 500
          }}
        >
          {tier.tier_name}
        </Typography>
      )
    },
    {
      key: 'sponsor_amount',
      sortKey: 'sponsor_amount',
      label: 'Amount (RM)',
      minWidth: 140,
      align: 'right',
      render: (tier) => (
        <Typography 
          variant="body2" 
          sx={{ color: alpha(theme.palette.common.white, 0.9) }}
        >
          {Number(tier.sponsor_amount).toLocaleString()}
        </Typography>
      )
    },
    {
      key: 'benefit',
      label: 'Benefit',
      minWidth: 300,
      render: (tier) => (
        <Typography 
          variant="body2" 
          sx={{ 
            color: alpha(theme.palette.common.white, 0.72),
            maxWidth: 420,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={tier.benefit || ''}
        >
          {tier.benefit || '—'}
        </Typography>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      sortable: false,
      minWidth: 120,
      render: (tier) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="View Tier" arrow>
            <IconButton
              onClick={(e) => { e.stopPropagation(); navigate(`/sponsor-tiers/${tier.id}`); }}
              size="small"
              sx={{
                color: theme.palette.info.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                }
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Tier" arrow>
            <IconButton
              onClick={(e) => { e.stopPropagation(); navigate(`/sponsor-tiers/edit/${tier.id}`); }}
              size="small"
              sx={{
                color: theme.palette.warning.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                }
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Tier" arrow>
            <IconButton
              onClick={(e) => { e.stopPropagation(); handleDelete(tier); }}
              size="small"
              sx={{
                color: theme.palette.error.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                }
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  if (!currentUser || !hasPartnershipAccess) return null;
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loading message="Loading sponsor tiers list" size={48} variant="primary" />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Header Section */}
      <Header
        title="Sponsor tiers"
        subtitle="Define tier names, amounts, and benefits for partner records"
        icon={StarsIcon}
        actionButton={
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={() => navigate('/sponsor-tiers/add')}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.22)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.34)',
              },
              borderRadius: 2,
              px: 2.5,
              py: 1.5,
            }}
          >
            Add Tier
          </Button>
        }
      />

      {/* Error Message */}
      <FeedbackMessage 
        message={error} 
        severity="error" 
        onClose={() => setError(null)} 
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: alpha('#450a0a', 0.92),
          border: '1px solid rgba(248,113,113,0.3)',
          boxShadow: '0 4px 20px rgba(239,68,68,0.12)',
          color: '#fca5a5',
          mb: 3,
        }}
      />

      {/* Success Message */}
      <FeedbackMessage 
        message={success} 
        severity="success" 
        onClose={() => setSuccess(null)} 
        sx={{
          fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500,
          background: alpha(SUCCESS, 0.12),
          border: `1px solid ${alpha(SUCCESS, 0.25)}`,
          color: alpha(SUCCESS, 0.95),
          mb: 3,
        }}
      />

      {/* Sponsor Tiers Table */}
      <ListBulkToolbar
        config={SPONSOR_TIER_BULK_CONFIG}
        currentUser={currentUser}
        selectedIds={selectedIds}
        onClearSelection={clearSelection}
        {...createBulkHandlers({
          endpoint: SPONSOR_TIER_BULK_CONFIG.endpoint,
          setItems: setTiers,
          selectedIds,
          clearSelection,
          setSuccess,
          successLabel: 'sponsor tier(s)',
          refetch: fetchTiers,
          setError
        })}
      />


      <TableList
        columns={columns}
        data={tiers}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        {...bulkTableProps(SPONSOR_TIER_BULK_CONFIG, currentUser, selectedIds, setSelectedIds)}
        loading={loading}
        emptyState={{
          icon: <StarsIcon />,
          title: 'No sponsor tiers found',
          description: 'Get started by creating your first sponsor tier.'
        }}
        onRowClick={(row) => navigate(`/sponsor-tiers/${row.id}`)}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={delOpen}
        onClose={() => { setDelOpen(false); setSelected(null); }}
        onConfirm={confirmDelete}
        loading={delLoading}
        title="Delete sponsor tier"
        itemName={selected?.tier_name}
        itemType="tier"
        description="You can only delete tiers that are not linked to any partner."
      />
    </Container>
  );
}